import Column from "@/atoms-react/column/Column.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { IFrameKYCAuthorizations } from "@/features/kyc/constants";
import { generateSessionURL } from "@/features/kyc/functions";
import { useKYC } from "@/features/kyc/hooks/use-kyc";
import {
  type CreateKYCDocument,
  type KYCDocumentType,
  type KYCSession,
  KYCSessionScenario,
  type KYCSessionURLParams
} from "@/features/kyc/types";
import { useUser } from "@/features/user/hooks/use-user";
import type React from "react";
import { useEffect } from "react";

type KYCSessionProps = {
  naturalPersonUUID?: string;
  KYCSessionState?: KYCSession;
  documentType?: KYCDocumentType;
  onValidated: (data: CreateKYCDocument) => void;
  params?: Partial<KYCSessionURLParams>;
  custom_scenario?: boolean;
  sessionLoading?: boolean;
} & React.ComponentPropsWithoutRef<"div">;

export const KYCSessionIFrame = ({
  naturalPersonUUID,
  KYCSessionState,
  documentType,
  onValidated = () => {},
  params,
  custom_scenario = false,
  sessionLoading = false,
  ...props
}: KYCSessionProps) => {
  const { useUserProfile } = useUser();
  const { refetch: refetchUser } = useUserProfile();
  const { KYCStateQuery } = useKYC();
  const { refetch: refetchKYCState } = KYCStateQuery(naturalPersonUUID || "");

  const handleIFrameKYCEvent = async (
    UUID: string,
    event: { data: { event: string; content: { url: string; success: boolean } } }
  ) => {
    if (event.data.event === "reload") {
      window.location.reload();
    }
    if (event.data.event === "end_session") {
      refetchKYCState();
      refetchUser();

      let senario = KYCSessionScenario.KYC;
      if (custom_scenario) {
        senario = KYCSessionScenario.Fusion;
      } else if (documentType?.need_manual_validation) {
        senario = KYCSessionScenario.ManualKYC;
      }
      const documentData: CreateKYCDocument = {
        sessionId: KYCSessionState?.sessionId ?? "",
        sessionType: KYCSessionState?.sessionType,
        sessionSenario: senario,
        document: {
          pieceType: documentType,
          number: "Document"
        },
        AbstractEntity: UUID
      };
      onValidated(documentData);
    }
  };
  useEffect(() => {
    window.addEventListener("message", (event) => {
      handleIFrameKYCEvent(naturalPersonUUID || "", event);
    });
    return () => {
      window.removeEventListener("message", (event) => {
        handleIFrameKYCEvent(naturalPersonUUID || "", event);
      });
    };
  }, []);
  return (
    <Column
      className={"m-0 h-screen w-full min-w-1 flex-1 justify-center self-center p-0"}
      {...props}
    >
      {sessionLoading && (
        <div className="flex flex-1 items-center justify-center">
          <Loader className="text-gray-500" />
        </div>
      )}
      {KYCSessionState && !sessionLoading && (
        <iframe
          width="100%"
          height="100%"
          src={generateSessionURL(KYCSessionState, params).toString()}
          allow={IFrameKYCAuthorizations}
          data-cy="algoreg-iframe"
          loading="eager"
        ></iframe>
      )}
    </Column>
  );
};
